import React, { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { Apis } from "../util/Apis";
import CustomLoading from "../components/CustomLoading";
import ContainerBackgroundWrapper from "../components/Backgrounds";
import { SiteContext } from "../context/SiteContext";
import GetHotSeatName from "../util/GetHotSeatName";
import axios from "axios";
import useGet from "../hooks/useGet";
import { useCountdown } from "../hooks/useCountdown";
import "./IdlePage.css";
import { usePost } from "../hooks/usePost";
import { getCurrencySymbol } from "../components/CardSorteo";
import Button from "../components/atoms/Buttons";
import { getCurrencyConfig } from "../components/services";
import warmupRaffleScene from "../components/virtualRaffle/warmup";

const IdleCountdown = ({ targetDate }) => {
  const [days, hours, minutes, seconds] = useCountdown(targetDate);
  const pending = [days, hours, minutes, seconds].every(Number.isFinite) && days + hours + minutes + seconds > 0;
  const pad = (value) => String(value).padStart(2, "0");
  return (
    <div>
      <p className="idle-description">{pending ? "El sorteo empezará dentro de:" : "El sorteo empezará en breve"}</p>
      {pending && <p className="idle-clock" aria-label={`${days} días, ${hours} horas, ${minutes} minutos, ${seconds} segundos`}>
        {days > 0 && `${days}d `}{pad(hours)}:{pad(minutes)}:{pad(seconds)}
      </p>}
    </div>
  );
};

const SwitchSorteoButton = ({ id, kind, getCupon }) => {
  const navigate = useNavigate();
  const hotSeat = kind === "HOT_SEAT";
  const automatic = hotSeat || kind === "VIRTUAL" || kind === "FLASH";
  return (
    <div className="idle-actions">
      <Button variant="outline" onClick={() => navigate("/sorteo-list")}>Inicio</Button>
      {automatic && <>
        <Button onClick={() => navigate(`${hotSeat ? "/sorteo-animation-hotseat/" : "/sorteo-animation/"}${id}`)}>Comenzar</Button>
        <Button variant="outline" onClick={() => navigate(`/sorteo/${id}/${hotSeat ? "machines" : "participantes"}`)}>{hotSeat ? "Máquinas" : "Participantes"}</Button>
      </>}
      {kind === "MANUAL" && <Button onClick={getCupon}>Validar</Button>}
    </div>
  );
};

const IdlePage = () => {

  const { salaId, setSorteosConf, currencyConfig: contextCurrencyConfig } = useContext(SiteContext);
  const navigate = useNavigate();
  const { id } = useParams();
  const currencyConfig = contextCurrencyConfig || getCurrencyConfig();

  const hotSeatName = GetHotSeatName();
  const [codCoupon, setCodCoupon] = useState("");
  const [message, setMessage] = useState(null);

  const { loading, data } = useGet({
    url: Apis.SORTEOS + "/sorteos/" + id,
    initialValue: [],
  });

  const { data: couponType } = useGet({
    url: `${Apis.PC}/coupon-type`,
  });

  const sorteosConf = useGet({
    url: `${Apis.SORTEOS}/sorteo-conf`,
    onSuccess: (data) => setSorteosConf(data),
  });

  const { mutate } = usePost({
    url: Apis.SORTEOS + "/sorteo-winner",
    onSuccess: () => {
      setTimeout(() => {
        navigate("/winner-view/" + id);
      }, 1000);
    },
  });

  // El sorteo virtual es el proximo paso desde aqui: su escena se adelanta
  // mientras esta pantalla espera, no cuando el usuario ya pulso "Comenzar".
  const virtual = data?.kind === "VIRTUAL" || data?.kind === "FLASH";
  useEffect(() => { if (virtual) warmupRaffleScene(); }, [virtual]);

  const sorteoData = useMemo(
    () =>
      Array.isArray(couponType) &&
      couponType.filter((item) => item.id === data?.couponTypeId).map((item) => item.name),
    [couponType, data?.couponTypeId]
  );

  const getCupon = () => {
    axios
      .get(`${Apis.PC}/coupon/${codCoupon}/validate-dto/v2`, {})
      .then((res) => {
        setMessage("Ganador encontrado");
        if (res.status === 200) saveWinner(res.data.confId, res.data.customerId, res.data.couponId);
      })
      .catch((err) => {
        if (err.response.status === 403) {
          setMessage("El cupón no es valido");
        } else {
          setMessage(err.response.data.message);
        }
      });
  };

  const saveWinner = async (confId, customerId, couponId) => {
    const params = {
      sorteoId: id,
      salaId: salaId,
      confId: confId,
      customerId: customerId,
      couponId: couponId,
      nroActa: sorteosConf.nroActa,
    };

    await mutate(params);
  };

  const cashPrize = data?.premioTipo === "soles" || data?.premioTipo === "CASH";

  return (
    <ContainerBackgroundWrapper showBackgroundCard={false}>
      {!loading && data ? (
        <main className="idle-screen" aria-label="Sorteo en espera">
          <div className="idle-content">
            <h1 className="idle-heading">{data.kind === "HOT_SEAT"
              ? (hotSeatName === "SORTEO HOT SEAT" ? "HOT SEAT" : hotSeatName)
              : (sorteoData?.length ? sorteoData : "SORTEO")}</h1>
            <div>
              <p className="idle-label">PREMIO</p>
              <p className="idle-prize" data-merchandise={!cashPrize}>
                {cashPrize ? `${getCurrencySymbol(data.currencyId, currencyConfig.symbol)} ${data.amount}` : `${data.amount} ${data.description || ""}`}
              </p>
            </div>
            {data.kind === "MANUAL" ? (
              <div className="idle-coupon">
                {message && <p className="idle-description" role="status">{message}</p>}
                <label className="idle-description" htmlFor="idle-coupon-number">Ingrese el número del cupón</label>
                {/* Pantalla de sala: el operador escribe el cupón apenas aparece, sin tocar el campo. */}
                {/* eslint-disable-next-line jsx-a11y/no-autofocus */}
                <input id="idle-coupon-number" type="text" maxLength={15} value={codCoupon} onChange={(e) => setCodCoupon(e.target.value)} autoFocus />
              </div>
            ) : <IdleCountdown key={data.fechaHora} targetDate={data.fechaHora} />}
            <SwitchSorteoButton id={id} kind={data.kind} getCupon={getCupon} />
          </div>
        </main>
      ) : <CustomLoading />}
    </ContainerBackgroundWrapper>
  );
};

export default IdlePage;
